/**
 * The impersonation target for an Event Hub request, worked out when the request is sent.
 *
 * Derived from the id token and the stored View as target — the same two inputs
 * ImpersonationContext and useEventHubLogin read — rather than pushed in from React. An earlier
 * version had EventHubLayout write it into module state during render, so it would be set before
 * a page's first fetch (child effects run before the parent's, so an effect would be too late).
 * But render is meant to be pure: under concurrent rendering React can start a render and discard
 * it, which would leave the global pointing at a target that was never committed. Reading at
 * request time has no ordering to get right and nothing to leave stale.
 *
 * The cost is decoding the token and reading localStorage per request. Both are microseconds,
 * against a network round trip.
 *
 * Gates, in order, each failing closed to "no header":
 *
 *   no token, or one that does not decode     → the request is unauthenticated anyway
 *   caller not in rocky-admin                 → may not use View as at all
 *   nothing stored for THIS caller            → not viewing as anyone (see impersonationStorage)
 *   caller not in event-hub-admin             → the service would 403 every request
 *
 * One case where this and the React state can differ: if the localStorage write failed when the
 * admin entered View as (quota, or a browser that refuses storage), the context still switches
 * but nothing is stored, so requests go out as the admin. The banner would then over-claim. That
 * is the safe direction — the other way round would send requests as someone the UI does not say.
 */

import { getIdToken } from '../../context/AuthContext';
import { decodeJwt } from '../../context/authStorage';
import { VIEW_AS_ADMIN_GROUP, loadImpersonation } from '../../context/impersonationStorage';
import { rolesOf } from '../utils/authorization';
import { eventHubLoginFor } from '../utils/eventHubLogin';

/** The alias to send as `X-Event-Hub-Impersonate`, or undefined for none. */
export function currentEventHubImpersonation(): string | undefined {
  const token = getIdToken();
  if (!token) return undefined;

  let uid: string | undefined;
  let groups: string[];
  try {
    const jwt = decodeJwt(token);
    uid = jwt.sub;
    groups = jwt.ldap_groups ?? [];
  } catch {
    return undefined;
  }
  if (!uid || !groups.includes(VIEW_AS_ADMIN_GROUP)) return undefined;

  const target = loadImpersonation(uid);
  const { login, impersonating } = eventHubLoginFor({
    uid,
    targetAlias: target,
    isImpersonating: target !== null && target !== uid,
    roles: rolesOf(groups),
  });
  return impersonating ? login : undefined;
}
